/**
 * Lane C0-OUTFIT2X: the native painter's toolkit — role colours (a part is drawn by the existing garment code in
 * stand-in colours, one hue per role, and each pixel read back as its role), material ramps (cotton, silk, leather,
 * gold, jade, hair, skin) in the game's palette, and per-pixel form light from the side the frame will be seen lit
 * from (`NativeCtx.light`), so the left-facing views are lit from the upper right too once flipped.
 */
import { fromHsl, shade, skinShade, toHsl } from '../../palette';
import type { JointName, Skeleton } from '../skeleton';
import type { NativeCtx } from './native';

// ── colours ──

/** A hex colour as a little-endian RGBA pixel. */
export function rgba(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return ((255 << 24) | (b << 16) | (g << 8) | r) >>> 0;
}
const R8 = (p: number) => p & 255;
const G8 = (p: number) => (p >>> 8) & 255;
const B8 = (p: number) => (p >>> 16) & 255;

/** Tone steps of a ramp: deep shadow, shadow, base, light, sheen. */
export type Level = -2 | -1 | 0 | 1 | 2;
export type Ramp = readonly [number, number, number, number, number];
export const tone = (r: Ramp, l: number): number => r[Math.max(-2, Math.min(2, Math.round(l))) + 2]!;

export type MatKind = 'cotton' | 'silk' | 'leather' | 'gold' | 'jade' | 'hair' | 'skin' | 'lacquer';

/**
 * A material's five tones from its base colour, the game's way (`shade`: shadows lean cool and richer, lights warm):
 * matte cotton has close tones, silk a wider spread and a bright sheen, leather a dark ramp with a hard highlight,
 * gold and jade their own ramps.
 */
export function ramp(hex: string, kind: MatKind): Ramp {
  if (kind === 'gold') return (['#5c3a12', '#9a6a22', '#d6a640', '#f0d27a', '#fff3c4'] as const).map(rgba) as unknown as Ramp;
  if (kind === 'skin') {
    const s = skinShade(hex);
    return [rgba(shade(s, -0.08)), rgba(s), rgba(hex), rgba(shade(hex, 0.05)), rgba(shade(hex, 0.09))];
  }
  const L = toHsl(hex)[2];
  const k = {
    cotton: [0.42, 0.22, 0.1, 0.2],
    silk: [0.45, 0.25, 0.16, 0.4],
    leather: [0.48, 0.26, 0.13, 0.32],
    jade: [0.4, 0.22, 0.14, 0.4],
    hair: [0.5, 0.28, 0.12, 0.3],
    lacquer: [0.5, 0.27, 0.15, 0.45],
  }[kind];
  const down = (f: number, lo: number, hi: number) => -Math.max(lo, Math.min(hi, L * f));
  const up = (f: number, lo: number) => Math.max(lo, (1 - L) * f);
  return [
    rgba(shade(hex, down(k[0]!, 0.07, 0.24))),
    rgba(shade(hex, down(k[1]!, 0.04, 0.13))),
    rgba(hex),
    rgba(shade(hex, up(k[2]!, 0.04))),
    rgba(shade(hex, up(k[3]!, 0.09))),
  ];
}

export interface Mat {
  kind: MatKind;
  ramp: Ramp;
}
export const mat = (hex: string, kind: MatKind): Mat => ({ kind, ramp: ramp(hex, kind) });

// ── roles ──

/** What a pixel of a part is: the role colour it was drawn in (cloth, trim, lining…). */
export type Role =
  | 'cloth'
  | 'trim'
  | 'inner'
  | 'sash'
  | 'accent'
  | 'wrap'
  | 'extra'
  | 'skin'
  | 'hair'
  | 'ribbon'
  /** Colour groups of fixed-colour art (headwear): `c0`, `c1`… */
  | `c${number}`;

/** One hue per role for the stand-in colours, far from the fixed golds, browns and greys parts use. */
const HUE: Record<string, number> = {
  cloth: 330,
  trim: 180,
  inner: 120,
  sash: 270,
  accent: 210,
  wrap: 90,
  extra: 300,
  skin: 150,
  hair: 0,
  ribbon: 240,
};

/** The stand-in colour of a role, at about the real colour's lightness (garment code that reads lightness reads true). */
export function sentinel(role: Role, real: string): string {
  const L = toHsl(real)[2];
  const hue = HUE[role] ?? (Number(role.slice(1)) * 67 + 15) % 360;
  return fromHsl([hue, 0.92, Math.max(0.42, Math.min(0.62, L))]);
}

/** A part's role colours, read back: each pixel to its role (the nearest stand-in or one of its shades). */
export class Roles {
  private readonly cand: { role: Role; dl: number; r: number; g: number; b: number }[] = [];
  private readonly memo = new Map<number, { role: Role; dl: number } | null>();
  constructor(entries: readonly (readonly [Role, string])[]) {
    for (const [role, hex] of entries) {
      for (let i = -50; i <= 50; i++) {
        const p = rgba(shade(hex, i / 100));
        this.cand.push({ role, dl: i / 100, r: R8(p), g: G8(p), b: B8(p) });
      }
      const p = rgba(skinShade(hex));
      this.cand.push({ role, dl: -0.1, r: R8(p), g: G8(p), b: B8(p) });
    }
  }
  /** The role of a pixel (null: not one of this part's role colours, keep it as drawn). */
  of(p: number): Role | null {
    return this.hit(p)?.role ?? null;
  }
  /** The role and the lightness step it was drawn at from the role's colour. */
  hit(p: number): { role: Role; dl: number } | null {
    if (!(p >>> 24)) return null;
    let hit = this.memo.get(p);
    if (hit !== undefined) return hit;
    let best = Infinity;
    hit = null;
    const r = R8(p);
    const g = G8(p);
    const b = B8(p);
    for (const c of this.cand) {
      const d = (c.r - r) ** 2 + (c.g - g) ** 2 + (c.b - b) ** 2;
      if (d < best) {
        best = d;
        hit = c;
      }
    }
    if (best > 40 * 40) hit = null;
    this.memo.set(p, hit && { role: hit.role, dl: hit.dl });
    return hit && { role: hit.role, dl: hit.dl };
  }
}

// ── geometry ──

/** Which part of the body a piece hangs on. */
export type Kind = 'trunk' | 'armA' | 'armB' | 'legA' | 'legB' | 'head';

const GROUPS: Record<Kind, readonly (readonly [JointName, JointName])[]> = {
  trunk: [
    ['neck', 'chest'],
    ['chest', 'waist'],
    ['waist', 'pelvis'],
  ],
  armA: [
    ['shoulderA', 'elbowA'],
    ['elbowA', 'wristA'],
  ],
  armB: [
    ['shoulderB', 'elbowB'],
    ['elbowB', 'wristB'],
  ],
  legA: [
    ['hipA', 'kneeA'],
    ['kneeA', 'ankleA'],
  ],
  legB: [
    ['hipB', 'kneeB'],
    ['kneeB', 'ankleB'],
  ],
  head: [['head', 'neck']],
};

type P = { x: number; y: number };

/** Distance from `p` to segment a–b and where along it (0–1) the nearest point lies. */
function toSeg(p: P, a: P, b: P): { d: number; t: number; nx: number; ny: number } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy || 1;
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2));
  const qx = a.x + dx * t;
  const qy = a.y + dy * t;
  const l = Math.sqrt(l2);
  return { d: Math.hypot(p.x - qx, p.y - qy), t, nx: -dy / l, ny: dx / l };
}

/** Joint in canvas pixels. */
export const J = (c: NativeCtx, n: JointName): P => ({ x: c.X(c.sk.joints[n].x) + 1, y: c.Y(c.sk.joints[n].y) + 1 });

/** The body part a piece hangs on: the bones most of its pixels lie nearest to (a piece over the middle of the body whose pixels lie mostly by the trunk and legs — a robe, a skirt — is the trunk's). */
export function kindOf(c: NativeCtx): Kind {
  const votes: Record<Kind, number> = { trunk: 0, armA: 0, armB: 0, legA: 0, legB: 0, head: 0 };
  const segs = (Object.keys(GROUPS) as Kind[]).flatMap((k) => GROUPS[k].map(([a, b]) => ({ k, a: J(c, a), b: J(c, b) })));
  for (let y = 0; y < c.bh; y += 2)
    for (let x = 0; x < c.bw; x += 2) {
      if (!(c.img[y * c.bw + x]! >>> 24)) continue;
      const p = { x: x + c.bx, y: y + c.by };
      let best = Infinity;
      let k: Kind = 'trunk';
      for (const s of segs) {
        const d = toSeg(p, s.a, s.b).d;
        if (d < best) {
          best = d;
          k = s.k;
        }
      }
      votes[k]++;
    }
  const has = (n: JointName) => {
    const p = J(c, n);
    return c.at(Math.round(p.x), Math.round(p.y)) >>> 24 > 0;
  };
  const all = Object.values(votes).reduce((a, b) => a + b, 0) || 1;
  if ((has('waist') || has('pelvis') || has('chest')) && (votes.trunk + votes.legA + votes.legB) / all >= 0.5) return 'trunk';
  return (Object.keys(votes) as Kind[]).reduce((a, b) => (votes[b] > votes[a] ? b : a));
}

/** Per pixel of a piece: where it lies across its form, toward the light (0 shadow edge … 1 lit edge), and how wide. */
export interface Across {
  t: number;
  w: number;
  /** Pixels to the lit edge and to the shadow edge. */
  lit: number;
  dark: number;
}

/**
 * Form across a piece at a pixel: a trunk is round about its upright axis (rows), a limb about its bone. Measured on
 * the piece's whole shape (`img`), so what lies in front of it does not cut it.
 */
export function makeAcross(c: NativeCtx, kind: Kind): (x: number, y: number) => Across {
  const L = { x: c.light, y: -0.9 };
  const segs = kind === 'trunk' || kind === 'head' ? [] : GROUPS[kind].map(([a, b]) => [J(c, a), J(c, b)] as const);
  const inside = (x: number, y: number) => c.at(Math.round(x), Math.round(y)) >>> 24 > 0;
  if (!segs.length) {
    // Round about an upright axis: the row's run, read from the measured shape.
    const sh = shapeOf(c);
    return (x, y) => {
      const i = (y - c.by) * c.bw + (x - c.bx);
      const l = sh.left[i] ?? 0;
      const r = sh.right[i] ?? 0;
      const lit = c.light > 0 ? r : l;
      const dark = c.light > 0 ? l : r;
      const w = l + r + 1;
      return { t: w > 1 ? dark / (w - 1) : 0.5, w, lit, dark };
    };
  }
  const march = (x: number, y: number, dx: number, dy: number) => {
    let n = 0;
    while (n < 24 && inside(x + dx * (n + 1), y + dy * (n + 1))) n++;
    return n;
  };
  return (x, y) => {
    let nx = 1;
    let ny = 0;
    if (segs.length) {
      let best = Infinity;
      for (const [a, b] of segs) {
        const s = toSeg({ x, y }, a, b);
        if (s.d < best) {
          best = s.d;
          nx = s.nx;
          ny = s.ny;
        }
      }
    }
    if (nx * L.x + ny * L.y < 0) {
      nx = -nx;
      ny = -ny;
    }
    const lit = march(x, y, nx, ny);
    const dark = march(x, y, -nx, -ny);
    const w = lit + dark + 1;
    return { t: w > 1 ? dark / (w - 1) : 0.5, w, lit, dark };
  };
}

/**
 * After a piece is painted: any pixel it shows still in a role colour (where the enlargement or the line pass moved a
 * stand-in colour) takes the colour of a painted neighbour.
 */
export function mendStandIns(c: NativeCtx, roles: Roles, painted: Uint8Array): void {
  const idx = (x: number, y: number) => (y - c.by) * c.bw + (x - c.bx);
  const inBox = (x: number, y: number) => x >= c.bx && y >= c.by && x < c.bx + c.bw && y < c.by + c.bh;
  for (let y = c.by; y < c.by + c.bh; y++)
    for (let x = c.bx; x < c.bx + c.bw; x++) {
      if (!c.vis(x, y) || painted[idx(x, y)] || !roles.hit(c.get(x, y))) continue;
      search: for (let r = 1; r <= 3; r++)
        for (let dy = -r; dy <= r; dy++)
          for (let dx = -r; dx <= r; dx++) {
            const qx = x + dx;
            const qy = y + dy;
            if (inBox(qx, qy) && painted[idx(qx, qy)] && c.vis(qx, qy)) {
              c.set(x, y, c.get(qx, qy));
              break search;
            }
          }
    }
}

/** Pixels of the piece's own shape straight below (0 at its lowest pixel in that column run). */
export function below(c: NativeCtx, x: number, y: number): number {
  const s = shapeOf(c);
  const lx = x - c.bx;
  const ly = y - c.by;
  return lx < 0 || ly < 0 || lx >= c.bw || ly >= c.bh ? 0 : s.down[ly * c.bw + lx]!;
}
export function above(c: NativeCtx, x: number, y: number): number {
  const s = shapeOf(c);
  const lx = x - c.bx;
  const ly = y - c.by;
  return lx < 0 || ly < 0 || lx >= c.bw || ly >= c.bh ? 0 : s.up[ly * c.bw + lx]!;
}

/**
 * The piece's shape measured once: for each pixel, how many of its own pixels run on to the left, right, up and down
 * (so the form light and the hem bands need no scanning per pixel).
 */
interface ShapeRuns {
  left: Int16Array;
  right: Int16Array;
  up: Int16Array;
  down: Int16Array;
}
const SHAPES = new WeakMap<Uint32Array, ShapeRuns>();
function shapeOf(c: NativeCtx): ShapeRuns {
  let s = SHAPES.get(c.img);
  if (s) return s;
  const { bw: W, bh: H, img } = c;
  const n = W * H;
  s = { left: new Int16Array(n), right: new Int16Array(n), up: new Int16Array(n), down: new Int16Array(n) };
  for (let y = 0; y < H; y++) {
    for (let x = 1; x < W; x++) {
      const i = y * W + x;
      if (img[i]! >>> 24 && img[i - 1]! >>> 24) s.left[i] = s.left[i - 1]! + 1;
    }
    for (let x = W - 2; x >= 0; x--) {
      const i = y * W + x;
      if (img[i]! >>> 24 && img[i + 1]! >>> 24) s.right[i] = s.right[i + 1]! + 1;
    }
  }
  for (let x = 0; x < W; x++) {
    for (let y = 1; y < H; y++) {
      const i = y * W + x;
      if (img[i]! >>> 24 && img[i - W]! >>> 24) s.up[i] = s.up[i - W]! + 1;
    }
    for (let y = H - 2; y >= 0; y--) {
      const i = y * W + x;
      if (img[i]! >>> 24 && img[i + W]! >>> 24) s.down[i] = s.down[i + W]! + 1;
    }
  }
  SHAPES.set(img, s);
  return s;
}

/** How much shadow a pixel takes from what lies nearer: under an edge above it, or cast down-and-away from the light. */
export function occlusion(c: NativeCtx, x: number, y: number): number {
  if (c.nearer(x, y - 1)) return -2;
  if (c.nearer(x, y - 2) || c.nearer(x + c.light, y - 1)) return -1;
  if (c.nearer(x + c.light * 2, y - 2)) return -1;
  return 0;
}

// ── motifs (canvas pixels) ──

/** A key fret (回纹): a square spiral in each six-pixel cell. */
export const FRET = ['#####.', '....#.', '.##.#.', '.#..#.', '.####.'];
/** Auspicious cloud curls (祥云), a scalloped band. */
export const CLOUD = ['..###...', '.#...#..', '#..#..#.', '#.##..##', '.#..##..'];
/** A five-petal plum blossom. */
export const BLOSSOM = ['.x.', 'xox', '.x.'];

export const motifAt = (m: readonly string[], u: number, v: number): string => {
  const row = m[((v % m.length) + m.length) % m.length]!;
  return row[((u % row.length) + row.length) % row.length]!;
};

export type { Skeleton };
